/**
 * Synthesizes `<configHome>/.credentials.json` from CLAUDE_CODE_OAUTH_TOKEN so
 * the interactive `claude` TUI starts authenticated in Conveyor-managed cloud
 * environments. The interactive TUI does NOT read CLAUDE_CODE_OAUTH_TOKEN —
 * that env var is honored only in headless/CI runs — so without a credentials
 * file a fresh pod's Connected TUI lands on the login-method picker.
 *
 * Safety policy: a credentials file whose `claudeAiOauth` carries a
 * refreshToken was written by a real interactive `/login` (the OAuth code flow
 * always issues one) and is never touched. Our synthesized file never has one —
 * that absence IS the "written by Conveyor" marker. If live verification ever
 * disproves that assumption, the fallback hardening is a sidecar marker file
 * (e.g. `<configHome>/.conveyor-credentials.json` holding a hash of the token
 * we wrote) — noted here so the next person doesn't reinvent the policy.
 *
 * Decision core is pure (planCredentialsWrite) so the overwrite policy can be
 * unit-tested without fs; the effectful wrappers never throw — a GCS-FUSE
 * hiccup must not kill PtySession.start().
 */

import { chmod, mkdir, readFile, rm, writeFile } from "node:fs/promises";
import { homedir } from "node:os";
import { join } from "node:path";
import { claudeConfigHome } from "./settings.js";

// `claude setup-token` tokens are valid for ~1 year. The exact expiry is
// unknowable from the token itself; claim a far-future expiresAt so the CLI
// never decides the credential is expired and attempts a refresh it cannot
// perform (no refreshToken). The file is re-synthesized on every spawn, so the
// window slides.
const SYNTH_TOKEN_TTL_MS = 365 * 24 * 60 * 60 * 1000;
// Rewrite our own file when it has less than this much runway left — a cheap
// staleness guard for a long-lived file on the persistent user-home mount.
const REFRESH_SKEW_MS = 30 * 24 * 60 * 60 * 1000;

export function claudeCredentialsPath(): string {
  return join(claudeConfigHome(), ".credentials.json");
}

/**
 * Only act inside Conveyor-managed cloud environments (GKE claudespace pods
 * export CLAUDESPACE_NAME in entrypoint.sh; GitHub Codespaces set
 * CODESPACE_NAME/CODESPACES) — never touch a developer's real ~/.claude.
 */
export function isConveyorCloudEnv(env: NodeJS.ProcessEnv = process.env): boolean {
  return Boolean(env.CLAUDESPACE_NAME || env.CODESPACE_NAME || env.CODESPACES);
}

export interface CredentialsWriteInput {
  isCloud: boolean;
  /** CLAUDE_CODE_OAUTH_TOKEN from the environment. */
  token: string | undefined;
  /** Current file contents; null when the file is missing. */
  existingRaw: string | null;
  now: number;
}

export type CredentialsPlan =
  | { action: "skip"; reason: "not-cloud" | "no-token" | "foreign-credentials" | "current" }
  | { action: "write"; contents: string };

interface ParsedOauth {
  accessToken: unknown;
  refreshToken: unknown;
  expiresAt: unknown;
}

function parseClaudeAiOauth(raw: string | null): ParsedOauth | null {
  if (!raw || raw.trim() === "") return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const oauth = (parsed as Record<string, unknown>).claudeAiOauth;
    if (typeof oauth !== "object" || oauth === null) return null;
    const record = oauth as Record<string, unknown>;
    return {
      accessToken: record.accessToken,
      refreshToken: record.refreshToken,
      expiresAt: record.expiresAt,
    };
  } catch {
    return null;
  }
}

/**
 * The synthesized shape is the empirical-iteration point: if a live pod's TUI
 * still shows the login picker, adjust scopes/subscriptionType HERE and
 * re-verify on a Claudespace diagnostic card.
 */
function buildSynthesizedCredentials(token: string, now: number): string {
  return JSON.stringify({
    claudeAiOauth: {
      accessToken: token,
      expiresAt: now + SYNTH_TOKEN_TTL_MS,
      scopes: ["user:inference", "user:profile"],
      subscriptionType: "max",
    },
  });
}

/** Pure overwrite-policy core — see the decision table in the tests. */
export function planCredentialsWrite(input: CredentialsWriteInput): CredentialsPlan {
  if (!input.isCloud) return { action: "skip", reason: "not-cloud" };
  if (!input.token) return { action: "skip", reason: "no-token" };

  const contents = buildSynthesizedCredentials(input.token, input.now);
  const existing = parseClaudeAiOauth(input.existingRaw);
  // Missing, empty, unparseable, or shapeless file — ours to (re)write.
  if (!existing) return { action: "write", contents };
  // A refreshToken means a genuine interactive /login — never clobber.
  if (typeof existing.refreshToken === "string" && existing.refreshToken.length > 0) {
    return { action: "skip", reason: "foreign-credentials" };
  }
  const fresh =
    existing.accessToken === input.token &&
    typeof existing.expiresAt === "number" &&
    existing.expiresAt > input.now + REFRESH_SKEW_MS;
  // Skip when current to avoid write churn on the GCS-FUSE mount.
  if (fresh) return { action: "skip", reason: "current" };
  return { action: "write", contents };
}

async function readRaw(path: string): Promise<string | null> {
  try {
    return await readFile(path, "utf8");
  } catch {
    return null;
  }
}

/**
 * Materialize CLAUDE_CODE_OAUTH_TOKEN as a credentials file for the
 * interactive TUI. Best-effort: logs and returns on any failure.
 */
export async function ensureClaudeCredentials(env: NodeJS.ProcessEnv = process.env): Promise<void> {
  try {
    const path = claudeCredentialsPath();
    const plan = planCredentialsWrite({
      isCloud: isConveyorCloudEnv(env),
      token: env.CLAUDE_CODE_OAUTH_TOKEN,
      existingRaw: await readRaw(path),
      now: Date.now(),
    });
    if (plan.action === "skip") return;
    await mkdir(claudeConfigHome(), { recursive: true });
    await writeFile(path, plan.contents, { encoding: "utf8", mode: 0o600 });
    // The GCS-FUSE CSI mount (file-mode=700) may reject chmod; it already
    // enforces owner-only access, so this is belt-and-braces for local disks.
    await chmod(path, 0o600).catch(() => {});
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    process.stderr.write(`[conveyor-agent] claude credentials sync failed: ${message}\n`);
  }
}

/**
 * Path of the CLI's main config. Lives at $HOME/.claude.json normally; when
 * CLAUDE_CONFIG_DIR is set the CLI relocates it under that directory.
 */
export function claudeJsonPath(): string {
  const configDir = process.env.CLAUDE_CONFIG_DIR;
  return configDir ? join(configDir, ".claude.json") : join(homedir(), ".claude.json");
}

/**
 * Pure decision core for the onboarding + trust seed. Returns the contents to
 * write, or null when no write is needed.
 *
 * Unlike the entrypoint's only-if-empty seeding, this MERGES into an existing
 * config: the CLI rewrites .claude.json during a first-run wizard (cache keys,
 * firstStartTime, …) WITHOUT setting hasCompletedOnboarding until the wizard
 * finishes. On a persistent user-home (GCS-FUSE) an abandoned wizard therefore
 * leaves a config that re-triggers onboarding on every subsequent pod — seen
 * live: credentials valid (`claude auth status` → loggedIn:true) yet the TUI
 * parked at the theme picker. All CLI-owned keys are preserved.
 *
 * `trustCwd` pre-accepts the folder-trust dialog for the workspace the TUI is
 * about to run in (`projects["<cwd>"].hasTrustDialogAccepted`) — the dialog is
 * the third first-run gate after theme and login, also seen parking a live
 * pod. Trusting the Conveyor-managed repo clone is definitionally correct in
 * this environment. The theme default is only applied when creating a fresh
 * config: the CLI drops unused keys on rewrite, and re-adding theme on every
 * spawn would cause a guaranteed write per spawn for zero behavior change
 * (the theme picker is gated on hasCompletedOnboarding, not theme).
 */
function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/** Parse the existing .claude.json; corrupt/non-object input becomes a fresh
 *  config (the CLI treats unparseable files as fatal, so replacing is a repair). */
function parseClaudeJson(existingRaw: string | null): Record<string, unknown> {
  if (!existingRaw || existingRaw.trim() === "") return {};
  try {
    return asRecord(JSON.parse(existingRaw));
  } catch {
    return {};
  }
}

/** Pre-accept the folder-trust dialog for `trustCwd`. Returns true if changed. */
function seedWorkspaceTrust(config: Record<string, unknown>, trustCwd: string): boolean {
  const projects = asRecord(config.projects);
  const entry = asRecord(projects[trustCwd]);
  if (entry.hasTrustDialogAccepted === true) return false;
  entry.hasTrustDialogAccepted = true;
  projects[trustCwd] = entry;
  config.projects = projects;
  return true;
}

export function planClaudeJsonSeed(existingRaw: string | null, trustCwd?: string): string | null {
  const config = parseClaudeJson(existingRaw);
  const isFresh = Object.keys(config).length === 0;
  let changed = false;
  if (config.hasCompletedOnboarding !== true) {
    config.hasCompletedOnboarding = true;
    changed = true;
  }
  // Pre-answer the "Bypass Permissions mode" warning dialog the CLI shows on
  // the first `--dangerously-skip-permissions` launch. Seeded unconditionally:
  // the flag is inert for plan-mode spawns, no human is at the keyboard in
  // cloud envs to press "Yes, I accept", and the permission mode can flip
  // between spawns sharing one persistent user-home.
  if (config.bypassPermissionsModeAccepted !== true) {
    config.bypassPermissionsModeAccepted = true;
    changed = true;
  }
  if (isFresh && typeof config.theme !== "string") {
    config.theme = "dark";
    changed = true;
  }
  if (trustCwd && seedWorkspaceTrust(config, trustCwd)) {
    changed = true;
  }
  return changed ? JSON.stringify(config) : null;
}

/**
 * Suppress the CLI's first-run wizard, the bypass-permissions warning dialog,
 * and (when `trustCwd` is given) the folder-trust dialog for that workspace
 * in Conveyor cloud envs so the spawned TUI boots straight into the REPL. Runs regardless of token presence
 * — with credentials the gates are pure friction; without them the
 * post-onboarding login screen is strictly better than the full wizard.
 * Best-effort, never throws.
 */
export async function ensureClaudeOnboarding(
  env: NodeJS.ProcessEnv = process.env,
  trustCwd?: string,
): Promise<void> {
  try {
    if (!isConveyorCloudEnv(env)) return;
    const path = claudeJsonPath();
    const contents = planClaudeJsonSeed(await readRaw(path), trustCwd);
    if (contents === null) return;
    await writeFile(path, contents, "utf8");
    // Read-back check: on network-backed homes (GCS-FUSE) a write can report
    // success while a subsequent read still serves stale content — which the
    // CLI's startup read would too, re-surfacing the dialogs this seed exists
    // to suppress (seen live 2026-07-02: seed wrote silently, the folder-trust
    // dialog parked a code review anyway). Log both outcomes so pod logs show
    // what the CLI is about to read.
    const verify = await readRaw(path);
    if (verify === contents) {
      process.stderr.write(
        `[conveyor-agent] claude onboarding seeded${trustCwd ? ` (trust: ${trustCwd})` : ""}\n`,
      );
    } else {
      process.stderr.write(
        `[conveyor-agent] claude onboarding seed read-back MISMATCH at ${path}: wrote ${contents.length}B, read ${verify?.length ?? 0}B — CLI may see stale config and park at a startup dialog\n`,
      );
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    process.stderr.write(`[conveyor-agent] claude onboarding seed failed: ${message}\n`);
  }
}

/**
 * Delete the credentials file only when it is ours (no refreshToken). Used
 * when a runtime key update switches the agent to ANTHROPIC_API_KEY — stale
 * subscription credentials must not keep authenticating the next TUI spawn.
 */
export async function removeConveyorCredentials(
  env: NodeJS.ProcessEnv = process.env,
): Promise<void> {
  try {
    if (!isConveyorCloudEnv(env)) return;
    const path = claudeCredentialsPath();
    const existing = parseClaudeAiOauth(await readRaw(path));
    if (existing && typeof existing.refreshToken === "string" && existing.refreshToken.length > 0) {
      return;
    }
    await rm(path, { force: true });
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    process.stderr.write(`[conveyor-agent] claude credentials removal failed: ${message}\n`);
  }
}
